import type { BandSet, HourRange } from '../types';
import { getBandGroups, hoursInBand } from './util';

/** Band geometry under edit — a row exists before its hours are picked. */
export type PartialHourRange = { [K in keyof HourRange]: HourRange[K] | null };

type BandError = { start_hour?: string };
type BandListErrors = BandError[] | string;

type BandsErrors = {
  WD?: BandListErrors;
  WE?: BandListErrors;
  ALL?: BandListErrors;
};

function validateBandList<T extends PartialHourRange>(bands: T[] | undefined): BandListErrors | undefined {
  if (!bands || bands.length === 0) return 'At least one band required';
  const errors: BandError[] = bands.map(() => ({}));
  const covered = new Set<number>();
  let prevEnd: number | null = null;
  bands.forEach((band, i) => {
    const { start_hour: start, end_hour: end } = band;
    // per-field required handles incomplete rows
    if (start == null || end == null) return;
    const hours = hoursInBand({ start_hour: start, end_hour: end });
    if (hours.some((h) => covered.has(h))) {
      errors[i].start_hour = 'Overlaps with previous band';
    } else if (prevEnd != null && start < prevEnd % 24) {
      // gaps are allowed, but a band may not start before the previous one ends.
      // end_hour 24 and 0 are both midnight, so a band continuing at 00:00 after
      // one ending at 24:00 (e.g. an hour-split overnight band) is not backward.
      errors[i].start_hour = 'Cannot start before the previous band ends';
    }
    hours.forEach((h) => covered.add(h));
    prevEnd = end;
  });
  return errors.some((e) => Object.keys(e).length > 0) ? errors : undefined;
}

function validateBands<T extends PartialHourRange>(bands: BandSet<T> | undefined | null): BandsErrors | undefined {
  if (!bands) return undefined;
  const errors: BandsErrors = {};
  for (const { dayType, bands: groupBands } of getBandGroups(bands)) {
    const groupErrors = validateBandList(groupBands);
    if (groupErrors) errors[dayType] = groupErrors;
  }
  return Object.keys(errors).length > 0 ? errors : undefined;
}

export function validateBandForm<T extends PartialHourRange>(values: {
  bands?: BandSet<T>;
}): { bands?: BandsErrors } | undefined {
  const bandErrors = validateBands(values.bands);
  return bandErrors ? { bands: bandErrors } : undefined;
}
